import { createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { CognitoUser, CognitoUserSession } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';
import { useNavigate } from 'react-router-dom';

import useRememberLastPath from '@/api/login/sideEffects/useRememberLastUserPath';
import { AdobeAnalyticsConst } from '@/hooks/AdobeAnalyticsHooks/adobeAnalyticsConstants/adobeAnalyticsConstants';
import { getAuthCheckAnalyticsPayload } from '@/hooks/AdobeAnalyticsHooks/getAuthCheckAnalyticsPayload';
import { useLatestRef } from '@/hooks/useLatestRef';
import { rememberPathAndSignOut, resetChatbotState } from './authSessionTransitions';

import { shouldShowDevTools } from '@/dev/utils/environment';
import { AppRoutes, checkUserAccess, getUserInfo, hasTokenExpired } from '@/utils';
import { isRefreshTokenExpiredError } from '@/utils/auth/isAuthError';

import useCountryOverrideStore from '@/dev/CountryOverrideDevTools/useCountryOverrideStore';
import { UserInfo } from '@/types/auth';
import { CallbackOptions } from '@/types/shared';

const AUTH_CHECK_MAX_RETRIES = 2;
const AUTH_CHECK_RETRY_BASE_MS = 300;

// If a freshly refreshed token is rejected again within this window, the refresh is not helping
// (e.g. the backend keeps returning 401), so force re-auth instead of looping refresh→refetch→401.
const REFRESH_RECOVERY_WINDOW_MS = 10_000;

// How often the watchdog re-checks token expiry. hasTokenExpired reports expiry 5 min early, so a
// once-a-minute check refreshes well before the token actually expires — a live tab never sends an
// expired token, so 401s stop happening at the source (no reliance on reading a server 401).
const SESSION_CHECK_INTERVAL_MS = 60_000;

const FORCE_SESSION_EXPIRED_KEY = 'ces:forceSessionExpired';

// 'expired' is a session that simply ran out and re-authenticates; 'failed' is a session that
// cannot be used at all and needs the access-denied page's clear-session recovery.
type RefreshOutcome = 'refreshed' | 'expired' | 'failed';

// Escape hatch for manual browser testing: forces the watchdog to treat the session as expired.
// Gated on the app's existing non-prod allowlist (localhost + dev1/dev2/test/staging + front*
// temp envs) via shouldShowDevTools, so it works on deployed test environments but never in prod.
// Fire-once: reading the flag also clears it, so the override triggers a single expiry cycle
// rather than trapping a tester in a redirect loop across reloads/re-logins.
const consumeDevForceSessionExpired = (): boolean => {
  try {
    if (!shouldShowDevTools() || typeof localStorage === 'undefined') {
      return false;
    }
    if (localStorage.getItem(FORCE_SESSION_EXPIRED_KEY) !== '1') {
      return false;
    }
    localStorage.removeItem(FORCE_SESSION_EXPIRED_KEY);
    return true;
  } catch {
    return false;
  }
};

const wait = (ms: number): Promise<void> =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// Amplify persists `CognitoIdentityServiceProvider.<clientId>.<user>.idToken` in localStorage while a user
// is signed in. Reading it lets us tell a genuine transient auth failure (a session exists) from a normal
// logged-out visitor (no session) — Amplify reports both as the same 'The user is not authenticated'
// rejection, so the stored session is the only reliable signal.
const getStoredCognitoIdToken = (): string | null => {
  try {
    const lastAuthUserKey = Object.keys(window.localStorage).find(
      key => key.startsWith('CognitoIdentityServiceProvider.') && key.endsWith('.LastAuthUser'),
    );
    if (!lastAuthUserKey) {
      return null;
    }
    const clientId = lastAuthUserKey.split('.')[1];
    const username = window.localStorage.getItem(lastAuthUserKey);
    return window.localStorage.getItem(`CognitoIdentityServiceProvider.${clientId}.${username}.idToken`);
  } catch {
    return null;
  }
};

const hasStoredCognitoSession = (): boolean => getStoredCognitoIdToken() !== null;

// The failure metric must count only a still-valid session we failed to authenticate — not a genuinely
// expired session (a normal re-login) — so it requires the stored token to be unexpired.
const hasValidStoredCognitoSession = (): boolean => {
  const idToken = getStoredCognitoIdToken();
  if (!idToken) {
    return false;
  }
  try {
    return !hasTokenExpired(idToken);
  } catch {
    return false;
  }
};

const pushAuthCheckEvent = (event: string, attempts?: number): void => {
  window.oneDataLayer = window.oneDataLayer || [];
  window.oneDataLayer.push(getAuthCheckAnalyticsPayload(event, attempts === undefined ? {} : { attempts }));
};

// A single transient failure of currentAuthenticatedUser() (e.g. Cognito token-endpoint throttling under
// load) must not strand a user who still holds a valid session, so retry with backoff before concluding
// "logged out" — but only when a stored session exists, so genuine logged-out visitors fail fast.
const getAuthenticatedUser = async (attempt = 0): Promise<CognitoUser> => {
  try {
    const currentUser: CognitoUser = await Auth.currentAuthenticatedUser();
    if (!currentUser.getSignInUserSession()) {
      throw new Error('Amplify session not hydrated yet');
    }
    if (attempt > 0) {
      pushAuthCheckEvent(AdobeAnalyticsConst.EVENTS.AUTH_CHECK_RECOVERED, attempt);
    }
    return currentUser;
  } catch (error) {
    if (hasStoredCognitoSession() && attempt < AUTH_CHECK_MAX_RETRIES) {
      await wait(AUTH_CHECK_RETRY_BASE_MS * 2 ** attempt);
      return getAuthenticatedUser(attempt + 1);
    }
    throw error;
  }
};

export type AuthContextType = {
  isLoggedIn: boolean | undefined;
  logout: () => void;
  token: string;
  refreshToken: (options?: CallbackOptions) => void;
  ensureValidSession: () => Promise<boolean>;
  isTokenValid: boolean;
  userInfo: UserInfo | null;
};

export const AuthContext = createContext<AuthContextType>({
  isLoggedIn: undefined,
  logout: () => {},
  token: null,
  refreshToken: () => {},
  ensureValidSession: () => Promise.resolve(false),
  isTokenValid: false,
  userInfo: null,
});

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const { rememberLastUserPath } = useRememberLastPath();

  const [user, setUser] = useState<CognitoUser>(null);
  const [token, setToken] = useState<string>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | undefined>(undefined);
  const overrideCountries = useCountryOverrideStore(state => state.countries);
  const refreshInFlightRef = useRef<Promise<boolean> | null>(null);
  const lastRefreshSuccessRef = useRef<number>(0);
  const navigate = useNavigate();

  // userInfo is derived (not stored) so it recomputes whenever the token refreshes or the simulated
  // countries change; the override narrows data scope only, never the real identity (sub/username).
  const userInfo = useMemo<UserInfo | null>(() => {
    if (!token) {
      return null;
    }
    try {
      return getUserInfo(token, overrideCountries);
    } catch {
      return null;
    }
  }, [token, overrideCountries]);

  // useNavigate's identity changes whenever the location does, so anything depending on it re-runs
  // after it navigates. Both consumers below are one-shot decisions — hydrate on mount, and a single
  // refresh outcome — and re-running them on their own navigation is what turns a redirect into a
  // loop, so they read navigate through a ref and stay stable.
  const navigateRef = useLatestRef(navigate);

  // Every route to access-denied is a de-auth transition, so it must also tear down the globally
  // mounted chatbot drawer (#5270); one helper keeps that guarantee in a single place.
  const navigateToAccessDenied = useCallback(() => {
    resetChatbotState();
    navigateRef.current(AppRoutes.ACCESS_DENIED);
  }, [navigateRef]);

  useEffect(() => {
    let isActive = true;

    const hydrate = async (): Promise<void> => {
      let currentUser: CognitoUser;
      try {
        currentUser = await getAuthenticatedUser();
      } catch {
        if (!isActive) {
          return;
        }

        // Report only a still-valid session we could not authenticate (the bug's residual failures);
        // logged-out and genuinely-expired sessions are intentionally excluded.
        if (hasValidStoredCognitoSession()) {
          pushAuthCheckEvent(AdobeAnalyticsConst.EVENTS.AUTH_CHECK_FAILURE);
        }
        setUser(null);
        setIsLoggedIn(false);
        // ProtectedRoute redirects to login without unmounting the global chatbot drawer (#5270).
        resetChatbotState();
        return;
      }

      try {
        const jwtToken = currentUser.getSignInUserSession().getIdToken().getJwtToken();
        checkUserAccess(jwtToken);
        // Decoded for validation only: the derived userInfo below turns the same failure into null.
        getUserInfo(jwtToken);
        if (!isActive) {
          return;
        }
        setUser(currentUser);
        setToken(jwtToken);
        setIsLoggedIn(true);
      } catch {
        if (!isActive) {
          return;
        }
        setUser(null);
        setIsLoggedIn(false);
        // Amplify returned a signed-in user we still cannot use: the token carries no CES entitlement
        // (USER_HAVE_NO_ACCESS_TO_ANY_RESOURCE) or does not decode at all (getJwtToken/checkUserAccess/
        // getUserInfo throw). Both need the access-denied clear-session recovery; falling through to
        // login would strand the user in a sign-in loop that rehydrates the same unreadable storage.
        navigateToAccessDenied();
      }
    };

    void hydrate();

    return () => {
      isActive = false;
    };
  }, [navigateToAccessDenied]);

  const logout = useCallback(() => {
    return rememberPathAndSignOut(rememberLastUserPath);
  }, [rememberLastUserPath]);

  // logout closes over the current location, so its identity changes on every navigation. Reading it
  // through a ref keeps ensureValidSession stable, which matters because the session watchdog below
  // re-runs checkSession every time that callback changes — depending on logout directly turns an
  // expiry into navigate → new location → new callback → check again → navigate, forever.
  const logoutRef = useLatestRef(logout);

  const refreshToken = useCallback(
    (callback?: CallbackOptions): void => {
      user.refreshSession(
        user.getSignInUserSession().getRefreshToken(),
        (err: unknown, session: CognitoUserSession) => {
          if (err) {
            logout();
            callback?.onError();
            return;
          }

          callback?.onSuccess();
          return setToken(session.getIdToken().getJwtToken());
        },
      );
    },
    [user, logout],
  );

  // A single controlled refresh attempt shared across every caller: many queries can fail with an
  // expired token at once, but only one refresh runs, and the outcome decides where the user lands.
  // A refresh token that simply ran out ('expired') is the ordinary end of a session, so it signs the
  // user out to the login page. Every other failure ('failed') means the stored session is unusable
  // rather than merely old, so it goes to the access-denied page for its clear-session recovery —
  // which is also why anything unrecognised must resolve to 'failed'.
  const ensureValidSession = useCallback((): Promise<boolean> => {
    if (refreshInFlightRef.current) {
      return refreshInFlightRef.current;
    }

    if (Date.now() - lastRefreshSuccessRef.current < REFRESH_RECOVERY_WINDOW_MS) {
      navigateToAccessDenied();
      return Promise.resolve(false);
    }

    const refreshPromise = new Promise<RefreshOutcome>(resolve => {
      if (!user) {
        resolve('failed');
        return;
      }
      // Reading the stored session throws outright when it is corrupt; without this the promise
      // would reject and callers would silently do nothing, leaving no way out of the broken state.
      try {
        user.refreshSession(
          user.getSignInUserSession().getRefreshToken(),
          (err: unknown, session: CognitoUserSession) => {
            if (isRefreshTokenExpiredError(err)) {
              resolve('expired');
              return;
            }
            if (err || !session) {
              resolve('failed');
              return;
            }
            setToken(session.getIdToken().getJwtToken());
            resolve('refreshed');
          },
        );
      } catch {
        resolve('failed');
      }
    })
      .then(outcome => {
        if (outcome === 'refreshed') {
          lastRefreshSuccessRef.current = Date.now();
          return true;
        }
        if (outcome === 'expired') {
          void logoutRef.current();
          return false;
        }
        navigateToAccessDenied();
        return false;
      })
      .finally(() => {
        refreshInFlightRef.current = null;
      });

    refreshInFlightRef.current = refreshPromise;
    return refreshPromise;
  }, [user, navigateToAccessDenied, logoutRef]);

  // Watchdog: proactively detect an expired/invalid token client-side (CORS-immune) rather than
  // waiting for a server 401 that this infra masks as an opaque CORS error. Checks on an interval,
  // on tab re-focus, and on mount; an idle tab that would otherwise keep sending a stale token is
  // refreshed (or, on failure, sent to re-auth) before it can produce a request storm.
  useEffect(() => {
    if (!token) {
      return undefined;
    }

    const checkSession = (): void => {
      let expired: boolean;
      try {
        expired = hasTokenExpired(token);
      } catch {
        expired = true;
      }
      if (expired || consumeDevForceSessionExpired()) {
        void ensureValidSession();
      }
    };

    checkSession();
    const intervalId = setInterval(checkSession, SESSION_CHECK_INTERVAL_MS);
    const handleVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') {
        checkSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [token, ensureValidSession]);

  const isTokenValid = useMemo(() => !!token && !hasTokenExpired(token), [token]);

  return (
    <AuthContext.Provider
      value={{
        isLoggedIn,
        logout,
        token,
        refreshToken,
        ensureValidSession,
        isTokenValid,
        userInfo,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => useContext(AuthContext);
